import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorFooter from "./DoctorFooter";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import { IconButton, Button } from "@mui/material";
import {
  Menu,
  MessageCircle,
  RefreshCw,
  Search,
  CalendarDays,
  Filter,
  UserRound,
  CheckCircle2,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import SimpleSnackbar from "../Snackbar";
import { getPatientsCache, setPatientsCache } from "../../cache/patientsCache";
import { io } from "socket.io-client";
import { notifyDoctorUnreadMessagesChanged } from "../../hooks/useDoctorUnreadMessages";

let socket;

const SECTIONS = ["Recent", "All Patients"];
const POLL_INTERVAL = 30000;
const PAGE_LIMIT = 30;

const buildPatientsData = (clients = []) => {
  const now = Date.now();
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

  const recent = [];
  const all = [];

  const isToday = (date) => {
    if (!date) return false;

    const d = new Date(date);
    const today = new Date();

    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };

  const getLastActivityTime = (client) => {
    const createdAt = new Date(client?.createdAt || 0).getTime();
    const lastMessage = client?.messages?.[client.messages.length - 1];
    const lastMessageTime = new Date(lastMessage?.timestamp || 0).getTime();

    return Math.max(createdAt || 0, lastMessageTime || 0);
  };

  for (const client of clients) {
    const messages = client?.messages || [];
    const lastMessage = messages[messages.length - 1];

    const lastMessageTime = new Date(lastMessage?.timestamp || 0).getTime();

    const isRecentByMessage =
      lastMessageTime && now - lastMessageTime <= TWENTY_FOUR_HOURS;

    const isRecentByCreation = isToday(client?.createdAt);

    all.push(client);

    if (isRecentByMessage || isRecentByCreation) {
      recent.push(client);
    }
  }

  const sortByActivity = (a, b) => {
    const aTime = getLastActivityTime(a);
    const bTime = getLastActivityTime(b);
    return bTime - aTime;
  };

  recent.sort(sortByActivity);
  all.sort(sortByActivity);

  return { recent, all };
};

const arePatientsDataEqual = (a, b) => {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const mergeUniquePatients = (existing = [], incoming = []) => {
  const map = new Map();

  [...existing, ...incoming].forEach((patient) => {
    if (patient?._id) {
      map.set(patient._id, patient);
    }
  });

  return Array.from(map.values());
};

const mergePatientsData = (prev, next) => {
  const merged = {
    recent: mergeUniquePatients(prev?.recent || [], next?.recent || []),
    all: mergeUniquePatients(prev?.all || [], next?.all || []),
  };

  const getLastActivityTime = (client) => {
    const createdAt = new Date(client?.createdAt || 0).getTime();
    const lastMessage = client?.messages?.[client.messages.length - 1];
    const lastMessageTime = new Date(lastMessage?.timestamp || 0).getTime();

    return Math.max(createdAt || 0, lastMessageTime || 0);
  };

  const sortByActivity = (a, b) =>
    getLastActivityTime(b) - getLastActivityTime(a);

  merged.recent.sort(sortByActivity);
  merged.all.sort(sortByActivity);

  return merged;
};

const getUnreadCount = (patient) => {
  return (
    patient?.messages?.filter((m) => m.sender === "client" && !m.read)
      .length || 0
  );
};

const formatDateTime = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const truncateText = (text = "", limit = 120) => {
  if (!text) return "";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}...`;
};

const PatientCard = ({
  patient,
  type,
  refreshPatients,
  setSnackMessage,
  setSnackColor,
}) => {
  const [showFullNote, setShowFullNote] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const navigate = useNavigate();

  const initials = (patient?.name || "NA")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const lastMessage = patient?.messages?.[patient.messages.length - 1];
  const unreadCount = getUnreadCount(patient);
  const accepted = patient?.assignedDoctor?.accepted;

  const acceptPatient = async () => {
    try {
      setAccepting(true);
      setSnackMessage("Accepting patient...");
      setSnackColor("info");

      const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

      if (!doctor?._id) {
        throw new Error("Doctor not found");
      }

      const res = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/clients-accept/${patient._id}`,
        { doctorId: doctor._id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      if (res.data?.success) {
        setSnackMessage("Patient accepted");
        setSnackColor("success");

        await refreshPatients({
          silent: true,
          showLoadingSnack: false,
          reset: true,
        });

        navigate(`/dashboard/patient/${patient._id}`);
      } else {
        throw new Error("Accept failed");
      }
    } catch (err) {
      console.error(err);
      setSnackMessage("Failed to accept patient");
      setSnackColor("error");
    } finally {
      setAccepting(false);
    }
  };

  const complaintText =
    patient?.workingDiagnosis || patient?.chiefComplaint || "No complaint";

  return (
    <article className="relative rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm">
      {unreadCount > 0 && (
        <span className="absolute right-4 top-4 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-2 text-xs font-semibold text-white">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}

      <div className="flex items-start gap-3 pr-8">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-slate-950">
            {patient?.name || "Unnamed Patient"}
          </h4>

          <p className="mt-1 text-xs text-slate-500">
            {patient?.age || "--"} yrs · {patient?.sex || "--"}
          </p>

          {accepted && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700">
              <CheckCircle2 size={12} />
              Accepted
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
          {patient?.workingDiagnosis ? "Diagnosis" : "Complaint"}
        </p>

        <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-700">
          {complaintText}
        </p>
      </div>

      {lastMessage && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-medium text-slate-400">Last message</p>

          <p className="mt-1 truncate text-sm text-slate-700">
            {lastMessage?.text || "Media message"}
          </p>

          <p className="mt-1 text-xs text-slate-400">
            {formatDateTime(lastMessage?.timestamp)}
          </p>
        </div>
      )}

      {type === "all patients" && !!patient?.clientHistory && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Notes
          </p>

          <p className="mt-2 text-sm leading-6 text-slate-600">
            {showFullNote
              ? patient.clientHistory
              : truncateText(patient.clientHistory, 100)}

            {patient.clientHistory.length > 100 && (
              <button
                type="button"
                className="ml-1 font-medium text-slate-950 underline"
                onClick={() => setShowFullNote((prev) => !prev)}
              >
                {showFullNote ? "Less" : "More"}
              </button>
            )}
          </p>
        </div>
      )}

      <div className="mt-6">
        {type === "recent" && !accepted ? (
          <Button
            variant="contained"
            fullWidth
            disabled={accepting}
            onClick={acceptPatient}
            sx={{
              bgcolor: "#075E54",
              color: "#fff",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
              boxShadow: "none",
              "&:hover": { bgcolor: "#064b43", boxShadow: "none" },
              "&:disabled": {
                bgcolor: "#e2e8f0",
                color: "#64748b",
              },
            }}
          >
            {accepting ? "Accepting..." : "Accept patient"}
          </Button>
        ) : (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<MessageCircle size={16} />}
            onClick={() => navigate(`/dashboard/patient/${patient._id}`)}
            sx={{
              borderColor: "#e2e8f0",
              color: "#0f172a",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
              boxShadow: "none",
              "&:hover": {
                borderColor: "#cbd5e1",
                bgcolor: "#f8fafc",
                boxShadow: "none",
              },
            }}
          >
            Open chat
          </Button>
        )}
      </div>
    </article>
  );
};

const PatientCardSkeleton = () => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="animate-pulse">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-slate-200" />
          <div className="flex-1">
            <div className="h-4 w-32 rounded bg-slate-200" />
            <div className="mt-2 h-3 w-20 rounded bg-slate-100" />
          </div>
        </div>

        <div className="mt-6 h-3 w-24 rounded bg-slate-200" />
        <div className="mt-3 h-3 w-full rounded bg-slate-100" />
        <div className="mt-2 h-3 w-4/5 rounded bg-slate-100" />

        <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50 p-3">
          <div className="h-3 w-20 rounded bg-slate-200" />
          <div className="mt-3 h-3 w-full rounded bg-slate-100" />
          <div className="mt-2 h-3 w-24 rounded bg-slate-100" />
        </div>

        <div className="mt-6 h-11 w-full rounded-xl bg-slate-200" />
      </div>
    </div>
  );
};

const DoctorPatientsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pollingRef = useRef(null);
  const hasHydratedFromUrl = useRef(false);
  const hasSkippedInitialFilterLoad = useRef(false);
  const loadMoreRef = useRef(null);

  const doctor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("doctor") || "{}");
    } catch {
      return {};
    }
  }, []);

  const doctorId = doctor?._id || "";

  const initialParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [searchName, setSearchName] = useState(initialParams.get("name") || "");
  const [startDate, setStartDate] = useState(initialParams.get("start") || "");
  const [endDate, setEndDate] = useState(initialParams.get("end") || "");
  const [assignedFilter, setAssignedFilter] = useState(
    initialParams.get("assigned") || ""
  );
  const [lastMessageFilter, setLastMessageFilter] = useState(
    initialParams.get("lastMsg") || ""
  );

  const [activeSection, setActiveSection] = useState(
    localStorage.getItem("active_section") || "Recent"
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Patients");

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const cachedPatientsData = getPatientsCache();
  const [trueTotalPatients, setTrueTotalPatients] = useState(
    cachedPatientsData?.all?.length || 0
  );

  const [patientsData, setPatientsData] = useState(
    cachedPatientsData || {
      recent: [],
      all: [],
    }
  );
  const hasVisiblePatientsRef = useRef(!!cachedPatientsData?.all?.length);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(
    !cachedPatientsData?.all?.length
  );

  useEffect(() => {
    if (!doctor?._id) return;

    if (!socket) {
      socket = io(process.env.REACT_APP_BACKEND_URL, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 3000,
        auth: {
          token: localStorage.getItem("token"),
        },
      });
    }

    const handleConnect = () => {
      socket.emit("join_doctor_room", doctor._id);
    };

    const handleConnectError = (err) => {
      console.log("Socket connection failed:", err.message);
    };

    const handleNewMessage = (msg) => {
      if (!msg?.sender_id) return;

      setPatientsData((prev) => {
        if (!prev) return prev;

        const updateList = (list = []) =>
          list.map((patient) => {
            if (patient?._id !== msg.sender_id) return patient;

            const existingMessages = Array.isArray(patient?.messages)
              ? patient.messages
              : [];

            const alreadyExists = existingMessages.some((m) => {
              return (
                String(m?.timestamp) === String(msg?.timestamp) &&
                m?.text === msg?.text &&
                m?.sender === msg?.sender
              );
            });

            return {
              ...patient,
              messages: alreadyExists
                ? existingMessages
                : [
                    ...existingMessages,
                    {
                      _id: msg._id,
                      sender: msg.sender,
                      text: msg.text,
                      timestamp: msg.timestamp,
                      media: Array.isArray(msg.media) ? msg.media : [],
                      twilioSid: msg.twilioSid,
                      read: false,
                      replyId: msg.replyId,
                      reply: msg.reply,
                    },
                  ],
            };
          });

        const next = {
          recent: updateList(prev.recent || []),
          all: updateList(prev.all || []),
        };

        setPatientsCache(next);
        return next;
      });

      if (msg.sender === "client") {
        notifyDoctorUnreadMessagesChanged();
      }
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("new_message", handleNewMessage);
    };
  }, [doctor?._id]);

  const updateURL = useCallback(() => {
    const params = new URLSearchParams();

    if (searchName.trim()) params.set("name", searchName.trim());
    if (startDate) params.set("start", startDate);
    if (endDate) params.set("end", endDate);
    if (assignedFilter) params.set("assigned", assignedFilter);
    if (lastMessageFilter) params.set("lastMsg", lastMessageFilter);

    navigate({ search: params.toString() }, { replace: true });
  }, [
    searchName,
    startDate,
    endDate,
    assignedFilter,
    lastMessageFilter,
    navigate,
  ]);

  const fetchPatients = useCallback(
    async ({
      silent = false,
      showLoadingSnack = true,
      page = 1,
      append = false,
    } = {}) => {
      if (!doctorId) return null;

      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          if (!silent && !hasVisiblePatientsRef.current) {
            setIsInitialLoading(true);
          }

          if (!silent && showLoadingSnack && !hasVisiblePatientsRef.current) {
            setSnackMessage("Loading patients...");
            setSnackColor("info");
          }
        }

        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/clients/search`,
          {
            doctorId,
            name: searchName.trim(),
            startDate,
            endDate,
            assigned: assignedFilter,
            lastMessage: lastMessageFilter,
            page,
            limit: PAGE_LIMIT,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") || null}`,
            },
          }
        );

        if (!res.data?.success) {
          throw new Error("Failed to fetch patients");
        }

        const nextChunk = buildPatientsData(res.data.clients || []);
        const pagination = res.data?.pagination || {};

        return {
          data: nextChunk,
          pagination: {
            total: pagination.total || 0,
            page: pagination.page || page,
            hasNextPage: !!pagination.hasNextPage,
          },
        };
      } catch (error) {
        console.error(error);

        if (!silent) {
          setSnackMessage("Failed to load patients");
          setSnackColor("error");
        }

        return null;
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsInitialLoading(false);
        }
      }
    },
    [
      doctorId,
      searchName,
      startDate,
      endDate,
      assignedFilter,
      lastMessageFilter,
    ]
  );

  const loadPatients = useCallback(
    async ({
      silent = false,
      showLoadingSnack = true,
      reset = true,
      page = 1,
      append = false,
    } = {}) => {
      const result = await fetchPatients({
        silent,
        showLoadingSnack,
        page,
        append,
      });

      if (!result) return;

      const { data, pagination } = result;

      setTrueTotalPatients(pagination.total);
      setCurrentPage(pagination.page);
      setHasNextPage(pagination.hasNextPage);

      setPatientsData((prev) => {
        const nextPatientsData = append ? mergePatientsData(prev, data) : data;

        setPatientsCache(nextPatientsData);
        hasVisiblePatientsRef.current = !!nextPatientsData?.all?.length;
        return nextPatientsData;
      });

    },
    [fetchPatients]
  );

  const clearFilters = () => {
    setSearchName("");
    setStartDate("");
    setEndDate("");
    setAssignedFilter("");
    setLastMessageFilter("");
    setCurrentPage(1);
    setHasNextPage(true);

    navigate({ search: "" }, { replace: true });
  };

  const pollPatients = useCallback(async () => {
    const result = await fetchPatients({
      silent: true,
      showLoadingSnack: false,
      page: 1,
      append: false,
    });

    if (!result) return;

    const { data, pagination } = result;
    const cachedData = getPatientsCache() || patientsData;

    if (!arePatientsDataEqual(cachedData, data)) {
      setPatientsData(data);
      setPatientsCache(data);
      hasVisiblePatientsRef.current = !!data?.all?.length;
      setTrueTotalPatients(pagination.total);
      setCurrentPage(pagination.page || 1);
      setHasNextPage(!!pagination.hasNextPage);
    }
  }, [fetchPatients, patientsData]);

  useEffect(() => {
    if (hasHydratedFromUrl.current) return;
    hasHydratedFromUrl.current = true;

    loadPatients({
      silent: true,
      showLoadingSnack: false,
      reset: true,
      page: 1,
      append: false,
    });
  }, [loadPatients]);

  useEffect(() => {
    if (!hasHydratedFromUrl.current) return;
    if (!hasSkippedInitialFilterLoad.current) {
      hasSkippedInitialFilterLoad.current = true;
      return;
    }

    updateURL();
    setCurrentPage(1);
    setHasNextPage(true);

    loadPatients({
      silent: true,
      showLoadingSnack: false,
      reset: true,
      page: 1,
      append: false,
    });
  }, [
    searchName,
    startDate,
    endDate,
    assignedFilter,
    lastMessageFilter,
    updateURL,
    loadPatients,
  ]);

  useEffect(() => {
    clearInterval(pollingRef.current);

    pollingRef.current = setInterval(() => {
      pollPatients();
    }, POLL_INTERVAL);

    return () => clearInterval(pollingRef.current);
  }, [pollPatients]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (
          firstEntry?.isIntersecting &&
          hasNextPage &&
          !isLoadingMore &&
          !isInitialLoading
        ) {
          loadPatients({
            silent: true,
            showLoadingSnack: false,
            reset: false,
            page: currentPage + 1,
            append: true,
          });
        }
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [currentPage, hasNextPage, isLoadingMore, isInitialLoading, loadPatients]);

  const counts = useMemo(
    () => ({
      Recent: patientsData?.recent?.length || 0,
      "All Patients": patientsData?.all?.length || 0,
    }),
    [patientsData]
  );

  const sectionData = useMemo(() => {
    return activeSection === "Recent" ? patientsData.recent : patientsData.all;
  }, [activeSection, patientsData]);

  const sectionType = activeSection === "Recent" ? "recent" : "all patients";

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Dashboard</p>
          <h1 className="text-base font-semibold text-slate-950">Patients</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden md:flex">
          <DoctorSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8 flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                    Patients
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    {Number(trueTotalPatients || 0).toLocaleString()} total
                    patients
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    <Filter size={16} />
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      loadPatients({
                        silent: true,
                        showLoadingSnack: false,
                        reset: true,
                        page: 1,
                        append: false,
                      })
                    }
                    className="inline-flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                  >
                    <RefreshCw size={16} />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Summary */}
              {/* <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Total patients</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {Number(trueTotalPatients || 0).toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Recent</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {Number(counts.Recent || 0).toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Loaded</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {Number(patientsData?.all?.length || 0).toLocaleString()}
                  </p>
                </div>
              </div> */}
            </div>

            {/* Filters */}
            <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                <div className="xl:col-span-2">
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Search
                  </label>

                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <Search size={16} className="text-slate-400" />

                    <input
                      type="text"
                      placeholder="Patient name"
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Start date
                  </label>

                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <CalendarDays size={16} className="text-slate-400" />

                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-transparent text-sm text-slate-800 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    End date
                  </label>

                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <CalendarDays size={16} className="text-slate-400" />

                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-transparent text-sm text-slate-800 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Filter
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={assignedFilter}
                      onChange={(e) => setAssignedFilter(e.target.value)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none"
                    >
                      <option value="">All</option>
                      <option value="assigned">Assigned</option>
                      <option value="unassigned">Unassigned</option>
                    </select>

                    <select
                      value={lastMessageFilter}
                      onChange={(e) => setLastMessageFilter(e.target.value)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none"
                    >
                      <option value="">Messages</option>
                      <option value="client">Client last</option>
                      <option value="doctor">Doctor last</option>
                    </select>
                  </div>
                </div>
              </div>
            </section>

            {/* Sections */}
            <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
              {SECTIONS.map((section) => {
                const displayCount =
                  section === "All Patients"
                    ? trueTotalPatients
                    : counts[section];

                return (
                  <button
                    key={section}
                    type="button"
                    onClick={() => {
                      localStorage.setItem("active_section", section);
                      setActiveSection(section);
                    }}
                    className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border px-4 text-sm font-medium transition ${
                      activeSection === section
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {section}

                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        activeSection === section
                          ? "bg-white/15 text-white"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {Number(displayCount || 0).toLocaleString()}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Active filter chips */}
            {(searchName ||
              startDate ||
              endDate ||
              assignedFilter ||
              lastMessageFilter) && (
              <div className="mb-6 flex flex-wrap gap-2">
                {searchName && (
                  <FilterChip
                    label={`Search: ${searchName}`}
                    onClear={() => setSearchName("")}
                  />
                )}

                {startDate && (
                  <FilterChip
                    label={`From: ${startDate}`}
                    onClear={() => setStartDate("")}
                  />
                )}

                {endDate && (
                  <FilterChip
                    label={`To: ${endDate}`}
                    onClear={() => setEndDate("")}
                  />
                )}

                {assignedFilter && (
                  <FilterChip
                    label={`Status: ${assignedFilter}`}
                    onClear={() => setAssignedFilter("")}
                  />
                )}

                {lastMessageFilter && (
                  <FilterChip
                    label={`Last: ${lastMessageFilter}`}
                    onClear={() => setLastMessageFilter("")}
                  />
                )}
              </div>
            )}

            {/* Empty state */}
            {sectionData.length === 0 && !isInitialLoading ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <UserRound size={20} className="text-slate-500" />
                </div>

                <h3 className="text-base font-semibold text-slate-950">
                  No patients found
                </h3>

                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Try changing your search or clearing the filters.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {sectionData.map((patient) => (
                    <PatientCard
                      key={patient._id}
                      patient={patient}
                      type={sectionType}
                      refreshPatients={loadPatients}
                      setSnackMessage={setSnackMessage}
                      setSnackColor={setSnackColor}
                    />
                  ))}

                  {isInitialLoading &&
                    sectionData.length === 0 &&
                    Array.from({ length: 6 }).map((_, index) => (
                      <PatientCardSkeleton key={`skeleton-${index}`} />
                    ))}
                </div>

                <div ref={loadMoreRef} className="h-10 w-full" />

                {isLoadingMore && (
                  <div className="py-6 text-center text-sm text-slate-400">
                    Loading more patients...
                  </div>
                )}

                {!hasNextPage && sectionData.length > 0 && (
                  <div className="py-6 text-center text-sm text-slate-400">
                    End of list
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>


      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        onClose={() => setSnackMessage("")}
      />
    </div>
  );
};

const FilterChip = ({ label, onClear }) => {
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
    >
      {label}
      <X size={13} />
    </button>
  );
};

export default DoctorPatientsPage;
